<script lang="ts">
	// UC-39: Pick a table (T15), a Sheet over Sell.
	//
	// Two taps from Sell in a shop with tables: Dine in, then the table. The
	// shop's tables come in name order from the server, "T2" before "T10", as
	// tiles in reading order; a shop may have 200, so the grid stays dense and
	// scrolls. One tap picks a table and closes the sheet, as a discount from
	// the list does. A table has no area and no busy or free mark: one table
	// may hold several orders at once.
	//
	// The list the till has shows at once; the read that opening starts
	// replaces it. With no list yet the compass shows, and a read that failed
	// with nothing in hand says so, with Retry.

	import Button from '../components/Button.svelte';
	import Loading from '../components/Loading.svelte';
	import NameTile from '../components/NameTile.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { message, type Language } from '../i18n/messages';
	import type { Table, TablesRead } from '../till/tables.svelte';

	interface Props {
		open: boolean;
		language: Language;
		tables: Table[];
		read: TablesRead;
		/** The table the cart names now, or ''. */
		current: string;
		onPick: (table: Table) => void;
		onRetry: () => void;
		onClose: () => void;
	}

	let { open, language, tables, read, current, onPick, onRetry, onClose }: Props = $props();
</script>

<Sheet
	{open}
	title={message(language, 'sellTable')}
	titleTestId="pick-table-title"
	{onClose}
	closeLabel={message(language, 'sheetClose')}
	closeTestId="pick-table-close"
	testId="pick-table-sheet"
>
	<div class="body" data-scroll="">
		{#if tables.length === 0 && read === 'loading'}
			<div class="wait">
				<Loading
					size="lg"
					label={message(language, 'tablesLoading')}
					caption={message(language, 'tablesLoading')}
				/>
			</div>
		{:else if tables.length === 0 && read === 'error'}
			<div class="failed" role="alert" data-testid="pick-table-failed">
				<p class="said">{message(language, 'tablesLoadFailed')}</p>
				<Button
					testId="pick-table-retry"
					label={message(language, 'retry')}
					tone="secondary"
					size="md"
					onPress={onRetry}
				/>
			</div>
		{:else}
			<ul class="tiles" role="list" aria-label={message(language, 'tablesTitle')}>
				{#each tables as table (table.id)}
					<NameTile
						testId="pick-table-{table.id}"
						name={table.name}
						chosen={table.id === current}
						onPick={() => onPick(table)}
					/>
				{/each}
			</ul>
		{/if}
	</div>
</Sheet>

<style>
	.body {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.wait {
		flex: 1;
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.failed {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-5);
	}

	.said {
		margin: 0;
		font-size: var(--text-base);
		font-weight: 600;
		color: var(--text-strong);
	}

	.tiles {
		margin: 0;
		padding: 0;
		list-style: none;
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(var(--key-w), 1fr));
		gap: var(--touch-gap);
	}
</style>
